import { t } from 'i18next'
import React from 'react'
import styled from 'styled-components'
import { easeInOutTransition, focusedInput } from '~/components/CommonStyles'
import { dimensions, spacing } from '~/constants/theme'

import Text, { FontStyle } from './Text'

interface RootWrapperProps {
  $direct?: 'vertical' | 'horizontal'
}

interface InputWrapperProps {
  $direct?: 'vertical' | 'horizontal'
  $error?: boolean
  $label?: string | null
  $noBorder?: boolean
}

const RootWrapper = styled.div<RootWrapperProps>`
  width: 100%;
  display: block;
  justify-content: center;
  align-items: center;
  text-align: center;
  background-color: var(--chakra-colors-transparent);
  flex-direction: ${(props) =>
    props.$direct === 'vertical' ? 'column' : 'row'};
  align-items: ${(props) =>
    props.$direct === 'vertical' ? 'flex-start' : 'center'};
  justify-content: ${(props) =>
    props.$direct === 'vertical' ? 'center' : 'space-between'};
`

const InputPrefixWrapper = styled.div`
  background-color: var(--chakra-colors-bg-muted-low);
  padding: 0px ${spacing.spacingMD};
  border-radius: ${dimensions.borderRadiusSM} 0px 0px
    ${dimensions.borderRadiusSM};
  display: flex;
  justify-content: center;
  color: var(--chakra-colors-fg-subtle);
  align-items: center;
`

const LabelWrapper = styled.div`
  width: 100%;
  padding-bottom: ${spacing.spacingSM};
  font-weight: bold;
  display: block;
`

const InputWrapper = styled.div<InputWrapperProps>`
  width: ${(props) => (props.$label ? '100%' : '100%')};
  border: ${(props) =>
    props.$noBorder
      ? 'none'
      : `1px solid var(--chakra-colors-${props.$error ? 'red-solid' : 'border-default'})`};
  border-radius: ${dimensions.borderRadiusSM};
  height: 40px;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: center;
  ${easeInOutTransition};

  &:focus-within {
    ${focusedInput as any}
    ${(props) =>
      props.$error && 'border-color: var(--chakra-colors-red-solid);'}
  }
`

const InputBox = styled.input`
  width: 100%;
  padding: ${spacing.spacingMD};
  display: block;
  border-radius: ${dimensions.borderRadiusSM};
  border: none;
  outline: none;
  font-family: 'Inter', Helvetica, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 17px;
  background-color: var(--chakra-colors-bg-default);
  color: var(--chakra-colors-fg-default);
  ${easeInOutTransition};

  &::placeholder,
  &::-webkit-input-placeholder {
    color: var(--chakra-colors-fg-muted);
  }
`

export interface InputProps {
  error?: boolean
  direction?: 'vertical' | 'horizontal'
  label?: string | null
  placeholder?: string
  type?: 'email' | 'file' | 'date' | 'password' | 'tel' | 'text'
  value?: string
  noBorder?: boolean
  onBlur?: () => void
  onChange?: (value: string) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  autoFocus?: boolean
  autoComplete?: string
  prefix?: string
  id?: string
  name?: string
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      noBorder = false,
      placeholder = t('global.input.defaultPlaceholder'),
      direction = 'horizontal',
      type = 'text',
      value = '',
      error = false,
      onBlur = () => {},
      onChange = () => {},
      onKeyDown,
      autoFocus = false,
      autoComplete,
      prefix,
      id,
      name,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
    },
    ref,
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value)
    }

    return (
      <RootWrapper $direct={direction}>
        {label && (
          <LabelWrapper>
            <Text
              size={FontStyle.Normal}
              text={label}
              color="var(--chakra-colors-fg-subtle)"
            />
          </LabelWrapper>
        )}
        <InputWrapper
          $direct={direction}
          $error={error}
          $label={label}
          $noBorder={noBorder}
        >
          {prefix && (
            <InputPrefixWrapper>
              <Text
                size={FontStyle.Small}
                text={prefix}
                color="var(--chakra-colors-fg-subtle)"
              />
            </InputPrefixWrapper>
          )}
          <InputBox
            id={id}
            name={name}
            placeholder={placeholder}
            ref={ref}
            type={type}
            value={value}
            onBlur={onBlur}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            autoFocus={autoFocus}
            autoComplete={autoComplete}
            aria-invalid={error || undefined}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
          />
        </InputWrapper>
      </RootWrapper>
    )
  },
)

Input.displayName = 'Input'

export default Input
